import { Images } from 'assets/images/images.index';
import { Button } from 'components/atoms/Button/Button';
import { CustomPropertyColorSelect } from 'components/atoms/CustomPropertyColorSelect/CustomPropertyColorSelect';
import { CustomPropertyDate } from 'components/atoms/CustomPropertyDate/CustomPropertyDate';
import { CustomPropertyLabel } from 'components/atoms/CustomPropertyLabel/CustomPropertyLabel';
import { CustomPropertySelect } from 'components/atoms/CustomPropertySelect/CustomPropertySelect';
import { Input, TypeInput } from 'components/atoms/Input/Input';

import { PopupExistProcess } from 'components/molecules/Popup-exist-porcess/Popup-exist-porcess';
import { typeProperty } from 'constants/app.constants';
import { useFormik } from 'formik';
import { useDateValidation } from 'hooks/useDateValidation';
import { useVehiclesColors } from 'hooks/useVehiclesColors';
import { IAppReducer } from 'models/reducer.interface';
import { IPropertiesInfoAttributes } from 'pages/Settings/models/Properties.interface';
import type { VehicleData } from 'pages/Settings/pages/Add-subscription/types/Add-subscription.types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { getVehicleFieldSortKey } from 'shared/constants/vehicle-form-order.constants';
import { getYear } from 'shared/constants/year.constants';
import { getDataByKey } from 'shared/services/user-add-subscription';
import { fetchVehiclesColors } from 'shared/services/vehicles-colors.service';
import { updateProductProperties } from 'shared/services/products';
import { getUserProperty } from 'shared/services/user';
import { getUserProperties } from 'shared/services/properties.service';
import { getVehicleDataKey, isVehicleType } from 'utils/properties.utils';
import {
  isValidVin,
  sanitizeVinInput,
  VIN_ERROR_MESSAGE,
} from 'utils/vin.utils';
import { setStateEditProperty } from 'store/actions/app.types';
import './Edit-property.scss';
import { IUpdatePropertiy } from './models/Edit-Property.interface';
import { HeaderWithSteps } from 'components/organisms/Header-with-steps/Header-with-steps';

export const EditProperty = () => {
  const { propertyId } = useParams();
  const dispatch = useDispatch();
  const user = useSelector((state: IAppReducer) => state.authReducer);
  const { branchReducer } = useSelector((state: IAppReducer) => state);
  const navigate = useNavigate();
  const [property, setProperty] = useState<IPropertiesInfoAttributes>();

  // Hook para validación de fechas dinámicas
  const { minDate, maxDate } = useDateValidation();

  const [popUp, setPopUp] = useState<{
    type: string;
    showModal: boolean;
    title: string;
    message: string;
    labelButton: string;
    labelButtonCancel: string;
  }>({
    type: '',
    showModal: false,
    title: '',
    message: '',
    labelButton: '',
    labelButtonCancel: '',
  });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [vehicleData, setVehicleData] = useState<VehicleData>({
    autos: {},
    motos: {},
  });
  const [selectedBrands, setSelectedBrands] = useState<Record<string, string>>(
    {}
  );
  const { colors: vehiclesColors } = useVehiclesColors();
  const currentYear = new Date().getFullYear();
  const [minYear, setMinYear] = useState<number>(
    getYear(branchReducer.branch)
  );
  const [maxYear, setMaxYear] = useState<number>(currentYear);
  const [
    formEditPropertyInitialValues,
    setFormEditPropertyInitialValues,
  ]: // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any = useState({});

  const handlePopUp = (type: 'exitsProcess' | undefined) => {
    const infoSetPopUp = {
      showModal: true,
      title: '',
      message: '',
      labelButton: '',
      labelButtonCancel: '',
      type: '',
    };
    if (type === 'exitsProcess') {
      infoSetPopUp.type = 'exitsProcess';
      infoSetPopUp.title = '¡Alto!';
      infoSetPopUp.message =
        '<strong>Está por dejar el proceso incompleto.</strong> Recuerda que al al dejarlo incompleto deberá iniciarlo nuevamente cuando regreses.';
      infoSetPopUp.labelButton = 'Continuar';
      infoSetPopUp.labelButtonCancel = 'Salir';
    } else {
      infoSetPopUp.showModal = false;
    }
    setPopUp({ ...infoSetPopUp });
  };

  const handleClosePopUp = () => {
    handlePopUp(undefined);
    navigate('/settings/properties');
  };

  useEffect(() => {
    (async () => {
      try {
        // Obtener las propiedades diligenciadas del usuario
        const response = await getUserProperty(user.uid, propertyId as string);
        const dataResponse: IPropertiesInfoAttributes = response[0].attributes;

        // Obtener todas las propiedades disponibles para este tipo de producto
        const allPropertiesResponse = await getUserProperties(
          dataResponse.tipo_producto.data.id
        );
        const allAvailableProperties = allPropertiesResponse.propiedades.data;

        // Crear un mapa de las propiedades ya diligenciadas por ID
        const existingPropertiesMap = new Map();
        dataResponse.property_attributes.forEach((element) => {
          existingPropertiesMap.set(element.propiedad.id, element);
        });

        // Crear la lista completa de propiedades combinando las existentes con las faltantes
        const completePropertyAttributes = allAvailableProperties.map(
          (availableProp: any) => {
            const existingProp = existingPropertiesMap.get(availableProp.id);

            if (existingProp) {
              // Si la propiedad ya existe, usar los datos existentes
              return {
                ...existingProp,
                propiedad: {
                  ...existingProp.propiedad,
                  ...availableProp.attributes,
                },
              };
            } else {
              // Si la propiedad no existe, crear un objeto mock con valor vacío
              return {
                id: 0, // ID temporal, se asignará al guardar
                valor: '',
                status: true,
                propiedad: {
                  id: availableProp.id,
                  name: availableProp.attributes.name,
                  type: availableProp.attributes.type,
                  required: availableProp.attributes.required,
                  length: availableProp.attributes.length,
                  large: availableProp.attributes.large,
                  name_intl_eng: availableProp.attributes.name_intl_eng,
                  order_number: availableProp.attributes.order_number,
                  endpoint: availableProp.attributes.endpoint,
                  parameters: availableProp.attributes.parameters,
                  file_order: availableProp.attributes.file_order,
                },
              };
            }
          }
        );

        // Crear el objeto de propiedades actualizado
        const updatedDataResponse = {
          ...dataResponse,
          property_attributes: completePropertyAttributes,
        };

        const tipoName = dataResponse.tipo_producto.data.attributes.name;
        const isVehicle = isVehicleType(dataResponse.tipo_producto.data.id);

        let brands: string[] = [];
        let vehiclesColorsMap: Record<string, string> = {};
        let vehicleDataFetched: VehicleData = { autos: {}, motos: {} };
        if (isVehicle) {
          vehicleDataFetched = await getDataByKey('brand_and_models');
          setVehicleData(vehicleDataFetched);
          const dataKey = getVehicleDataKey(dataResponse.tipo_producto.data.id);
          brands = Object.keys(vehicleDataFetched[dataKey] || {});
          vehiclesColorsMap = await fetchVehiclesColors();
        }

        // Crear los valores iniciales; si el valor del servidor no está en la lista (marca/modelo/color), usar ''
        const initialValues: any = {};
        let sanitizedMarcaVal = '';
        completePropertyAttributes.forEach((element: any) => {
          const fieldKey = `${tipoName}__${element.propiedad.name}__${element.propiedad.id}__${element.id}`;
          const rawVal = (element.valor || '') as string;
          let value = rawVal;

          if (isVehicle) {
            const propName = (element.propiedad.name || '').toLowerCase();
            if (propName === 'marca') {
              value = brands.includes(rawVal) ? rawVal : '';
              sanitizedMarcaVal = value;
            } else if (propName === 'modelo') {
              const dataKey = getVehicleDataKey(
                dataResponse.tipo_producto.data.id
              );
              const modelList: string[] =
                vehicleDataFetched[dataKey]?.[sanitizedMarcaVal] ?? [];
              value =
                Array.isArray(modelList) && modelList.includes(rawVal)
                  ? rawVal
                  : '';
            } else if (propName.toLowerCase() === 'color') {
              if (rawVal) {
                const exactMatch =
                  Object.keys(vehiclesColorsMap).includes(rawVal);
                const caseMatch = Object.keys(vehiclesColorsMap).find(
                  (k) => k.toLowerCase() === rawVal.toLowerCase()
                );
                value = exactMatch ? rawVal : caseMatch ?? rawVal;
              } else {
                value = '';
              }
            }
          }

          initialValues[fieldKey] = value;
        });

        setProperty({ ...updatedDataResponse });
        setFormEditPropertyInitialValues(initialValues);
        const productIdAttrs =
          dataResponse.productos_usuario?.data?.[0]?.attributes?.product_id
            ?.data?.attributes;
        if (productIdAttrs) {
          const { min_property_age, max_property_age } = productIdAttrs;
          const numMax = Number(max_property_age);
          const numMin = Number(min_property_age);
          const candidateMin = !isNaN(numMax) ? currentYear - numMax : minYear;
          const candidateMax = !isNaN(numMin) ? currentYear - numMin : maxYear;
          setMinYear(Math.min(candidateMin, candidateMax));
          setMaxYear(Math.max(candidateMin, candidateMax));
        }
        if (isVehicle && sanitizedMarcaVal) {
          setSelectedBrands((prev) => ({
            ...prev,
            [propertyId as string]: sanitizedMarcaVal,
          }));
        }
      } catch (error) {
        console.error('Error loading property data:', error);
        // En caso de error, mantener el comportamiento original
        const response = await getUserProperty(user.uid, propertyId as string);
        const dataResponse: IPropertiesInfoAttributes = response[0].attributes;
        const initialValues: any = {};
        dataResponse.property_attributes.forEach((element) => {
          initialValues[
            `${dataResponse.tipo_producto.data.attributes.name}__${element.propiedad.name}__${element.propiedad.id}__${element.id}`
          ] = element.valor || '';
        });
        setProperty({ ...dataResponse });
        setFormEditPropertyInitialValues(initialValues);
      }
    })();
  }, []);

  /** Requeridos para vehículos: Placa, Año, Marca, Modelo. Resto opcionales (Color, VIN, Fecha marbete). */
  const isRequiredVehicleField = (
    name: string | undefined,
    nameIntl: string | undefined
  ) => {
    const n = (name ?? '').toLowerCase();
    const ni = (nameIntl ?? '').toLowerCase();
    return (
      n === 'placa' ||
      ni === 'placa' ||
      n === 'año' ||
      ni === 'año' ||
      n === 'marca' ||
      ni === 'marca' ||
      n === 'modelo' ||
      ni === 'modelo'
    );
  };

  /** Valor vacío o placeholder de select (Marca/Modelo) no cuenta como selección válida. */
  const isRequiredValueEmpty = (
    value: unknown,
    fieldName: string | undefined
  ) => {
    if (value === undefined || value === null) return true;
    if (typeof value !== 'string') return !value;
    const trimmed = value.trim();
    if (trimmed === '') return true;
    const name = (fieldName ?? '').toLowerCase();
    if (name === 'marca' && trimmed.toLowerCase() === 'seleccionar marca')
      return true;
    if (name === 'modelo' && trimmed.toLowerCase() === 'seleccionar modelo')
      return true;
    return false;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formEditPropertyValidate = (values: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errors: any = {};
    property?.property_attributes.forEach((item) => {
      const fieldKey = `${property.tipo_producto.data.attributes.name}__${item.propiedad.name}__${item.propiedad.id}__${item.id}`;
      const value = values[fieldKey];
      const required = isRequiredVehicleField(
        item.propiedad.name,
        item.propiedad.name_intl_eng
      );
      if (required && isRequiredValueEmpty(value, item.propiedad.name)) {
        errors[fieldKey] = 'Campo requerido';
      } else if (
        value !== undefined &&
        value !== '' &&
        String(value).trim() !== ''
      ) {
        const isVin =
          item.propiedad.name?.toLowerCase() === 'vin' ||
          item.propiedad.name_intl_eng?.toLowerCase() === 'vin';
        const isYear =
          item.propiedad.name?.toLowerCase() === 'año' ||
          item.propiedad.name_intl_eng?.toLowerCase() === 'año';
        if (isVin && !isValidVin(String(value))) {
          errors[fieldKey] = VIN_ERROR_MESSAGE;
        } else if (isYear) {
          const num = Number(value);
          if (isNaN(num) || num < minYear || num > maxYear) {
            errors[fieldKey] = `El año debe estar entre ${minYear} y ${maxYear}`;
          }
        }
      }
    });
    return errors;
  };
  const formEditProperty = useFormik({
    initialValues: formEditPropertyInitialValues,
    enableReinitialize: true,
    validate: formEditPropertyValidate,
    validateOnMount: true,
    onSubmit: async (values) => {
      try {
        setErrorMessage(''); // Limpiar mensaje de error previo
        const data: IUpdatePropertiy[] = [{ atributos: [] }];

        Object.keys(values)
          .filter((keyValue) => {
            const propertyTypeId = +keyValue.split('_')[4];
            const isMarbeteField =
              property?.property_attributes.find(
                (attr) => attr.propiedad.id === propertyTypeId
              )?.propiedad?.name_intl_eng === 'date_marbete';

            if (isMarbeteField) {
              const isVehicle = property?.tipo_producto.data.id
                ? isVehicleType(property.tipo_producto.data.id)
                : false;
              const isPR = branchReducer.branch.toLowerCase() === 'pr';
              return isVehicle && isPR;
            }
            return true;
          })
          .forEach((keyValue, index) => {
            const propertyIdFromKey = +keyValue.split('_')[6];
            const propertyTypeId = +keyValue.split('_')[4];
            const propertyValue = values[keyValue];

            // Verificar si la propiedad tiene valor válido
            const hasValidValue =
              propertyValue &&
              ((typeof propertyValue === 'string' &&
                propertyValue.trim() !== '') ||
                (typeof propertyValue === 'number' && !isNaN(propertyValue)) ||
                propertyValue instanceof Date ||
                (typeof propertyValue === 'object' && propertyValue !== null));

            const isNewProperty = propertyIdFromKey === 0;
            const isDelete = !hasValidValue; // Si no tiene valor válido, marcar para eliminar

            // Convertir el valor a string para el servidor
            let valorToSend = '';
            if (hasValidValue) {
              if (propertyValue instanceof Date) {
                valorToSend = propertyValue.toISOString().split('T')[0]; // Formato YYYY-MM-DD
              } else if (
                typeof propertyValue === 'object' &&
                propertyValue !== null
              ) {
                valorToSend = JSON.stringify(propertyValue);
              } else {
                valorToSend = String(propertyValue);
              }
            }

            const attributeData: any = {
              valor: valorToSend,
              status: !isDelete, // Si se va a eliminar, status = false
              propiedad: propertyTypeId,
              propiedad_usuario: +(propertyId as string),
              fieldNew: isNewProperty, // Identificar si es una propiedad nueva
              isDelete: isDelete, // Identificar si se debe eliminar
            };

            // Solo agregar ID si no es una nueva propiedad (ID !== 0)
            if (!isNewProperty) {
              attributeData.id = propertyIdFromKey;
            }

            data[0].atributos[index] = attributeData;
          });

        // Filtrar atributos undefined y propiedades nuevas sin valor
        // No enviar propiedades nuevas que no tienen valor (como date_marbete vacío)
        data[0].atributos = data[0].atributos.filter(
          (attr) => attr !== undefined && !(attr.fieldNew && attr.isDelete)
        );

        await updateProductProperties(
          user.uid,
          {
            data: [...data],
          },
          user.agentAuthData?.userId,
          user.agentAuthData?.sourceApp ?? 'meraki-frontend',
          user.agentAuthData?.sourceOrigin ?? (window.location.origin + '/')
        );
        dispatch(setStateEditProperty(+(propertyId as string)));
        navigate('/settings/properties');
      } catch (error: any) {
        console.log('error', error);
        if (
          error?.response?.data?.error?.message
            ?.toLowerCase()
            ?.includes('duplicada')
        ) {
          setErrorMessage(
            error?.response?.data?.error?.details?.message ||
              'La tabillia ya existe en otro vehículo.'
          );
        } else {
          setErrorMessage(
            'Error al guardar los cambios. Por favor, inténtalo de nuevo.'
          );
        }
      } finally {
        formEditProperty.setSubmitting(false);
      }
    },
  });

  // Cuando el VIN tiene 17 caracteres válidos, quitar el error para que no quede bloqueado
  useEffect(() => {
    if (!property) return;
    const vinAttr = property.property_attributes.find(
      (item) =>
        item.propiedad.name?.toLowerCase() === 'vin' ||
        item.propiedad.name_intl_eng?.toLowerCase() === 'vin'
    );
    if (!vinAttr) return;
    const vinFieldKey = `${property.tipo_producto.data.attributes.name}__${vinAttr.propiedad.name}__${vinAttr.propiedad.id}__${vinAttr.id}`;
    const value = formEditProperty.values[vinFieldKey];
    if (typeof value === 'string' && value.length === 17 && isValidVin(value)) {
      formEditProperty.setFieldError(vinFieldKey, undefined);
    }
  }, [property, formEditProperty.values, formEditProperty.setFieldError]);

  const getTitleProperty = () => {
    const propertyFind = property?.property_attributes.find(
      (p) =>
        p.propiedad.name === 'Placa' || p.propiedad.name === 'Nombre Mascota'
    );
    const titleTypeProduct = {
      '1': 'Placa',
      '2': 'Hogar',
      '3': 'Mascota',
      '4': 'Placa',
    }[property?.tipo_producto.data.id || '1'];
    return `${titleTypeProduct} ${propertyFind?.valor}`;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getTypeProperty = (): any => {
    return (
      (property && { ...typeProperty }[property.tipo_producto.data.id]) || ''
    );
  };

  return (
    <>
      <HeaderWithSteps
        navbarTemplate="light"
        notProgress={true}
        onClick={() => handlePopUp('exitsProcess')}
        steps={[
          {
            description: `${getTitleProperty()}`,
            state: 'active',
            link: '/',
            permissionToNavigate: false,
          },
        ]}
      />
      <section className="edit__property__container">
        <>
          <img
            className="edit__property__icon"
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            src={(Images as any)[getTypeProperty() || 'auto']}
            alt="Icono Propiedad"
          />
          <span>
            Comunícate con nuestro Servicio al Cliente para actualizar o editar
            los datos de tu {property && getTypeProperty()}.
          </span>
          <div className="edit__property__form__container grid rg-0 grid__col-6 ">
            {property &&
              property.property_attributes
                .filter((item) => {
                  if (item.propiedad?.name_intl_eng === 'date_marbete') {
                    const isVehicle =
                      property.tipo_producto.data.attributes.name.toLowerCase() ===
                      'vehiculos';
                    return (
                      isVehicle && branchReducer.branch.toLowerCase() === 'pr'
                    );
                  }
                  return true;
                })
                .sort((a, b) => {
                  const isVehicle =
                    property.tipo_producto.data.attributes.name?.toLowerCase() ===
                    'vehiculos';
                  if (!isVehicle) return 0;
                  return (
                    getVehicleFieldSortKey(
                      a.propiedad.name,
                      a.propiedad.name_intl_eng
                    ) -
                    getVehicleFieldSortKey(
                      b.propiedad.name,
                      b.propiedad.name_intl_eng
                    )
                  );
                })
                .map((item, index) => {
                  const fieldName = `${property.tipo_producto.data.attributes.name}__${item.propiedad.name}__${item.propiedad.id}__${item.id}`;
                  const fieldValue = formEditProperty.values[fieldName] || '';
                  const isVehicle =
                    property.tipo_producto.data.attributes.name?.toLowerCase() ===
                    'vehiculos';
                  const isVinField =
                    item.propiedad.name?.toLowerCase() === 'vin' ||
                    item.propiedad.name_intl_eng?.toLowerCase() === 'vin';
                  const gridClass =
                    isVehicle && isVinField
                      ? 'grid__span-6'
                      : isVehicle
                      ? 'grid__span-3'
                      : item.propiedad.large
                      ? 'grid__span-6'
                      : 'grid__span-3';
                  const brands =
                    Object.keys(vehicleData.autos).length > 0
                      ? Object.keys(vehicleData.autos)
                      : Object.keys(vehicleData.motos);
                  const selectedBrand =
                    selectedBrands[propertyId as string] || '';
                  const models =
                    vehicleData.autos[selectedBrand] ||
                    vehicleData.motos[selectedBrand] ||
                    [];

                  if (item.propiedad.type === 'date') {
                    return (
                      <div
                        key={`${item.propiedad.name}__${index}`}
                        className={`edit-property__field ${gridClass}`}
                      >
                        <CustomPropertyLabel
                          htmlFor={`edit-vehicle-${item.propiedad.name}`}
                          required={item.propiedad.required}
                        >
                          {item.propiedad.name}
                        </CustomPropertyLabel>
                        <CustomPropertyDate
                          id={`edit-vehicle-${item.propiedad.name}`}
                          value={fieldValue}
                          onChange={(value) => {
                            formEditProperty.setFieldValue(fieldName, value);
                            formEditProperty.setFieldTouched(fieldName, true);
                            if (formEditProperty.errors[fieldName]) {
                              const newErrors = {
                                ...formEditProperty.errors,
                              };
                              delete newErrors[fieldName];
                              formEditProperty.setErrors(newErrors);
                            }
                          }}
                          placeholder={item.propiedad.name}
                          required={item.propiedad.required}
                          minDate={minDate}
                          maxDate={maxDate}
                          hideLabel
                        />
                      </div>
                    );
                  }
                  if (isVehicle && item.propiedad.name === 'Marca') {
                    return (
                      <div
                        key={`${item.propiedad.name}__${index}`}
                        className={`edit-property__field ${gridClass}`}
                      >
                        <CustomPropertyLabel
                          htmlFor={`edit-vehicle-${item.propiedad.name}`}
                          required={item.propiedad.required}
                        >
                          {item.propiedad.name}
                        </CustomPropertyLabel>
                        <CustomPropertySelect
                          id={`edit-vehicle-${item.propiedad.name}`}
                          value={fieldValue}
                          onChange={(value) => {
                            formEditProperty.setFieldValue(fieldName, value);
                            formEditProperty.setFieldTouched(fieldName, true);
                            setSelectedBrands((prev) => ({
                              ...prev,
                              [propertyId as string]: value,
                            }));
                            // Al cambiar marca, limpiar modelo (la lista de modelos depende de la marca)
                            const modeloAttr =
                              property?.property_attributes.find(
                                (attr) => attr.propiedad.name === 'Modelo'
                              );
                            if (modeloAttr) {
                              const modeloFieldKey = `${property.tipo_producto.data.attributes.name}__${modeloAttr.propiedad.name}__${modeloAttr.propiedad.id}__${modeloAttr.id}`;
                              formEditProperty.setFieldValue(
                                modeloFieldKey,
                                ''
                              );
                            }
                            setTimeout(() => {
                              formEditProperty.validateForm();
                            }, 0);
                          }}
                          options={brands}
                          placeholder={`Seleccionar ${item.propiedad.name.toLowerCase()}`}
                        />
                      </div>
                    );
                  }
                  if (isVehicle && item.propiedad.name === 'Modelo') {
                    return (
                      <div
                        key={`${item.propiedad.name}__${index}`}
                        className={`edit-property__field ${gridClass}`}
                      >
                        <CustomPropertyLabel
                          htmlFor={`edit-vehicle-${item.propiedad.name}`}
                          required={item.propiedad.required}
                        >
                          {item.propiedad.name}
                        </CustomPropertyLabel>
                        <CustomPropertySelect
                          id={`edit-vehicle-${item.propiedad.name}`}
                          value={fieldValue}
                          onChange={(value) => {
                            formEditProperty.setFieldValue(fieldName, value);
                            formEditProperty.setFieldTouched(fieldName, true);
                            setTimeout(() => {
                              formEditProperty.validateForm();
                            }, 0);
                          }}
                          options={models}
                          placeholder={`Seleccionar ${item.propiedad.name.toLowerCase()}`}
                        />
                      </div>
                    );
                  }
                  if (
                    isVehicle &&
                    (item.propiedad.name?.toLowerCase() === 'color' ||
                      item.propiedad.name_intl_eng?.toLowerCase() === 'color')
                  ) {
                    return (
                      <div
                        key={`${item.propiedad.name}__${index}`}
                        className={`edit-property__field ${gridClass}`}
                      >
                        <CustomPropertyLabel
                          htmlFor={`edit-vehicle-${item.propiedad.name}`}
                          required={item.propiedad.required}
                        >
                          {item.propiedad.name}
                        </CustomPropertyLabel>
                        <CustomPropertyColorSelect
                          id={`edit-vehicle-${item.propiedad.name}`}
                          value={fieldValue}
                          onChange={(value) => {
                            formEditProperty.setFieldValue(fieldName, value);
                            formEditProperty.setFieldTouched(fieldName, true);
                          }}
                          colors={vehiclesColors}
                          placeholder={`Seleccionar ${item.propiedad.name.toLowerCase()}`}
                        />
                      </div>
                    );
                  }
                  if (item.propiedad.name === 'Año') {
                    return (
                      <div
                        key={`${item.propiedad.name}__${index}`}
                        className={`edit-property__field ${gridClass}`}
                      >
                        <CustomPropertyLabel
                          htmlFor={`edit-vehicle-${item.propiedad.name}`}
                          required={item.propiedad.required}
                        >
                          {item.propiedad.name}
                        </CustomPropertyLabel>
                        <Input
                          label={item.propiedad.name}
                          id={`edit-vehicle-${item.propiedad.name}`}
                          type="number"
                          name={fieldName}
                          placeholder={`Año entre ${minYear} y ${maxYear}`}
                          onChange={formEditProperty.handleChange}
                          onBlur={formEditProperty.handleBlur}
                          value={fieldValue}
                          isTouched={
                            formEditProperty.touched[fieldName] as boolean
                          }
                          errorInput={
                            formEditProperty.errors[fieldName] as string
                          }
                          errorLabel={
                            formEditProperty.errors[fieldName] as string
                          }
                          isRequired={item.propiedad.required}
                          form={formEditProperty}
                          isDisabled={!user.agentId}
                          hideLabel
                        />
                      </div>
                    );
                  }
                  const isVin =
                    item.propiedad.name?.toLowerCase() === 'vin' ||
                    item.propiedad.name_intl_eng?.toLowerCase() === 'vin';
                  if (isVin) {
                    const initialVinValue = (formEditProperty.initialValues[
                      fieldName
                    ] ?? '') as string;
                    const vinDisabled =
                      !user.agentId ||
                      (isValidVin(initialVinValue) &&
                        fieldValue === initialVinValue);
                    return (
                      <div
                        key={`${item.propiedad.name}__${index}`}
                        className={`edit-property__field edit-property__field--vin ${gridClass}`}
                      >
                        <Input
                          label={item.propiedad.name}
                          id={`edit-vehicle-${item.propiedad.name}`}
                          type="alphanumeric"
                          name={fieldName}
                          onChange={(e) => {
                            const sanitized = sanitizeVinInput(e.target.value);
                            formEditProperty.setFieldValue(
                              fieldName,
                              sanitized
                            );
                            formEditProperty.setFieldTouched(fieldName, true);
                            if (
                              sanitized.length === 17 &&
                              isValidVin(sanitized)
                            ) {
                              formEditProperty.setFieldError(
                                fieldName,
                                undefined
                              );
                            } else {
                              setTimeout(() => {
                                formEditProperty.validateField(fieldName);
                              }, 0);
                            }
                          }}
                          onBlur={formEditProperty.handleBlur}
                          value={fieldValue}
                          isTouched={
                            formEditProperty.touched[fieldName] as boolean
                          }
                          errorInput={
                            formEditProperty.errors[fieldName] as string
                          }
                          errorLabel={
                            formEditProperty.errors[fieldName] as string
                          }
                          isRequired={item.propiedad.required}
                          maxLength={17}
                          form={formEditProperty}
                          isDisabled={vinDisabled}
                        />
                      </div>
                    );
                  }
                  return (
                    <div
                      key={`${item.propiedad.name}__${index}`}
                      className={`edit-property__field ${gridClass}`}
                    >
                      <CustomPropertyLabel
                        htmlFor={`edit-vehicle-${item.propiedad.name}`}
                        required={item.propiedad.required}
                      >
                        {item.propiedad.name}
                      </CustomPropertyLabel>
                      <Input
                        label={item.propiedad.name}
                        id={`edit-vehicle-${item.propiedad.name}`}
                        type={item.propiedad.type as TypeInput}
                        name={fieldName}
                        placeholder={
                          item.propiedad.name === 'Placa' ? 'ABC123' : undefined
                        }
                        onChange={formEditProperty.handleChange}
                        onBlur={formEditProperty.handleBlur}
                        value={fieldValue}
                        isTouched={
                          formEditProperty.touched[fieldName] as boolean
                        }
                        errorInput={
                          formEditProperty.errors[fieldName] as string
                        }
                        errorLabel={
                          formEditProperty.errors[fieldName] as string
                        }
                        isRequired={item.propiedad.required}
                        maxLength={item.propiedad.length}
                        form={formEditProperty}
                        textTransform={
                          item.propiedad.name === 'Placa' ? true : false
                        }
                        isDisabled={!user.agentId}
                        hideLabel
                      />
                    </div>
                  );
                })}
          </div>
          {errorMessage && (
            <div
              className="edit__property__error"
              style={{
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span style={{ color: 'red', fontSize: '14px' }}>
                {errorMessage}
              </span>
              <button
                onClick={() => setErrorMessage('')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'red',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                }}
              >
                ×
              </button>
            </div>
          )}
          <Button
            color="primary"
            size="medium"
            template="primary"
            typeStyle="rounded"
            type="submit"
            label="guardar cambios"
            isDisabled={
              formEditProperty.isSubmitting || !formEditProperty.isValid
            }
            isLoading={formEditProperty.isSubmitting}
            onClick={formEditProperty.handleSubmit}
          ></Button>
        </>
      </section>
      {popUp.showModal && (
        <PopupExistProcess
          title={popUp.title}
          message={popUp.message}
          labelButton={popUp.labelButton}
          labelButtonCancel={popUp.labelButtonCancel}
          type="warning"
          closePopUp={() => handlePopUp(undefined)}
          exitProcess={handleClosePopUp}
        />
      )}
    </>
  );
};
